import { IMAGE_PROVIDER_TYPES } from 'js/config/consts';
import {
  ImageCategory,
  ImageAssetSortOrder,
  ImageLibraryQueryKey,
  ScribeImageElementModelProps,
  UploadProvider,
  VSCAssetImageData,
  VSCAssetPageItem,
  RatingOption,
  AddAIGenerationTrigger,
  VSCAssetImageSourceName,
  AddUserImageToProjectEventTrigger,
  VSLibrarySourceType
} from 'js/types';

export const ADD_USER_UPLOAD_IMAGE_TO_CANVAS = 'ADD_USER_UPLOAD_IMAGE_TO_CANVAS';
export const ADD_USER_IMAGE_TO_SCRIBE = 'ADD_USER_IMAGE_TO_SCRIBE';
export const DELETE_USER_IMAGE_REQUESTED = 'DELETE_USER_IMAGE_REQUESTED';
const DELETE_USER_IMAGE_FAILURE = 'DELETE_USER_IMAGE_FAILURE';
export const DELETE_USER_IMAGE_SUCCESS = 'DELETE_USER_IMAGE_SUCCESS';
export const GET_IMAGE_LIBRARIES = 'GET_IMAGE_LIBRARIES';
export const GET_IMAGE_LIBRARIES_SUCCESS = 'GET_IMAGE_LIBRARIES_SUCCESS';
export const GET_IMAGE_LIBRARIES_FAILURE = 'GET_IMAGE_LIBRARIES_FAILURE';
export const ADD_LIBRARY_IMAGE_TO_CANVAS = 'ADD_LIBRARY_IMAGE_TO_CANVAS';
export const UPLOAD_IMAGE_FILES = 'UPLOAD_IMAGE_FILES';
export const UPLOAD_LIBRARY_IMAGE_ASSET = 'UPLOAD_LIBRARY_IMAGE_ASSET';
export const UPLOAD_IMAGE_ASSET_ON_LOAD = 'UPLOAD_IMAGE_ASSET_ON_LOAD';
export const UPLOAD_IMAGE_SUCCESS = 'UPLOAD_IMAGE_SUCCESS';
const UPLOAD_IMAGE_FAILED = 'UPLOAD_IMAGE_FAILED';
export const UPLOAD_USER_IMAGE_ASSET = 'UPLOAD_USER_IMAGE_ASSET';
export const START_REPLACE_IMAGE = 'START_REPLACE_IMAGE';
export const CLEAR_REPLACE_IMAGE = 'CLEAR_REPLACE_IMAGE';
export const REPLACE_USER_IMAGE_ELEMENT = 'REPLACE_USER_IMAGE_ELEMENT';
export const REPLACE_LIBRARY_IMAGE_ELEMENT = 'REPLACE_LIBRARY_IMAGE_ELEMENT';
export const REPLACE_UPLOADED_IMAGE_ELEMENT = 'REPLACE_UPLOADED_IMAGE_ELEMENT';
export const FULLY_VECTORIZE_IMAGE_AND_REPLACE_ELEMENT = 'FULLY_VECTORIZE_IMAGE_AND_REPLACE_ELEMENT';
export const PREPEND_USER_IMAGES_LIST = 'PREPEND_USER_IMAGES_LIST';
export const UPLOAD_IMAGE_TO_LIBRARY = 'UPLOAD_IMAGE_TO_LIBRARY';
export const RENAME_USER_IMAGE = 'RENAME_USER_IMAGE';
export const RENAME_USER_IMAGE_SUCCESS = 'RENAME_USER_IMAGE_SUCCESS';
export const FETCH_IMAGE_LIBRARY_PAGE = 'FETCH_IMAGE_LIBRARY_PAGE';
export const FETCH_IMAGE_LIBRARY_PAGE_SUCCESS = 'FETCH_IMAGE_LIBRARY_PAGE_SUCCESS';
export const FETCH_IMAGE_LIBRARY_PAGE_FAILURE = 'FETCH_IMAGE_LIBRARY_PAGE_FAILURE';
export const INVALIDATE_IMAGE_LIBRARY_CACHE = 'INVALIDATE_IMAGE_LIBRARY_CACHE';

interface AddUserImageToCanvasAction {
  type: typeof ADD_USER_UPLOAD_IMAGE_TO_CANVAS;
  file: File;
  filename: string;
  scribeId: number;
  canvasSize: { width: number; height: number };
  uploadMethod: string;
}

export const addUserImageToCanvas = (
  file: File,
  filename: string,
  scribeId: number,
  canvasSize: { width: number; height: number },
  uploadMethod: string
): AddUserImageToCanvasAction => ({
  type: ADD_USER_UPLOAD_IMAGE_TO_CANVAS,
  file,
  filename,
  scribeId,
  canvasSize,
  uploadMethod
});

export interface ReplaceUserImageElementAction {
  type: typeof REPLACE_USER_IMAGE_ELEMENT;
  imageId: number;
  imageType: string;
  scribeId: number;
  provider: UploadProvider;
  canvasSize: { height: number; width: number };
  selectedLibrary: string;
  filename: string;
  elementId: string;
  isPremium?: boolean;
}

export const replaceUserImageElement = ({
  imageId,
  imageType,
  scribeId,
  canvasSize,
  selectedLibrary,
  filename,
  elementId
}: Omit<ReplaceUserImageElementAction, 'type' | 'provider'>): ReplaceUserImageElementAction => ({
  type: REPLACE_USER_IMAGE_ELEMENT,
  imageId,
  imageType,
  scribeId,
  provider: IMAGE_PROVIDER_TYPES.USER,
  canvasSize,
  selectedLibrary,
  filename,
  elementId
});

export interface ReplaceLibraryImageElementAction {
  type: typeof REPLACE_LIBRARY_IMAGE_ELEMENT;
  imageId: string;
  imageType: string;
  scribeId: number;
  provider: UploadProvider;
  sourceType: VSLibrarySourceType;
  canvasSize: { height: number; width: number };
  selectedLibrary: string;
  filename: string;
  elementId: string;
  isPremium?: boolean;
}

export const replaceLibraryImageElement = ({
  imageId,
  imageType,
  scribeId,
  provider,
  sourceType,
  canvasSize,
  selectedLibrary,
  filename,
  elementId,
  isPremium
}: Omit<ReplaceLibraryImageElementAction, 'type'>): ReplaceLibraryImageElementAction => ({
  type: REPLACE_LIBRARY_IMAGE_ELEMENT,
  imageId,
  imageType,
  scribeId,
  provider,
  sourceType,
  canvasSize,
  selectedLibrary,
  filename,
  elementId,
  isPremium
});

export interface ReplaceUploadedImageElementAction {
  type: typeof REPLACE_UPLOADED_IMAGE_ELEMENT;
  imageType: string;
  scribeId: number;
  provider: UploadProvider;
  canvasSize?: { height: number; width: number };
  filename: string;
  elementId: string;
  file: File;
}

export const replaceUploadedImageElement = ({
  imageType,
  scribeId,
  canvasSize,
  filename,
  elementId,
  file
}: Omit<ReplaceUploadedImageElementAction, 'type' | 'provider'>): ReplaceUploadedImageElementAction => ({
  type: REPLACE_UPLOADED_IMAGE_ELEMENT,
  imageType,
  scribeId,
  provider: IMAGE_PROVIDER_TYPES.LIBRARY,
  canvasSize,
  filename,
  elementId,
  file
});

export interface AddUserImageToScribeAction {
  type: typeof ADD_USER_IMAGE_TO_SCRIBE;
  imageId: number;
  imageType: string;
  scribeId: number;
  provider: UploadProvider;
  canvasSize: { height: number; width: number };
  selectedLibrary: { title: string };
  filename: string;
  rating?: RatingOption;
  eventTrigger?: AddUserImageToProjectEventTrigger | AddAIGenerationTrigger;
  imageLibrarySource?: VSCAssetImageSourceName;
  actionTime: Date;
}

export const addUserImageToScribe = (
  imageId: number,
  imageType: string,
  scribeId: number,
  canvasSize: { height: number; width: number },
  selectedLibrary: { title: string },
  filename: string,
  provider = IMAGE_PROVIDER_TYPES.USER,
  rating?: RatingOption,
  eventTrigger?: AddUserImageToProjectEventTrigger | AddAIGenerationTrigger,
  imageLibrarySource?: VSCAssetImageSourceName
): AddUserImageToScribeAction => ({
  type: ADD_USER_IMAGE_TO_SCRIBE,
  actionTime: new Date(),
  imageId,
  imageType,
  scribeId,
  provider,
  canvasSize,
  selectedLibrary,
  filename,
  rating,
  eventTrigger,
  imageLibrarySource
});

export interface AddLibraryImageToCanvasAction {
  type: typeof ADD_LIBRARY_IMAGE_TO_CANVAS;
  imageId: string;
  imageType: string;
  sourceType: VSLibrarySourceType;
  scribeId: number;
  provider: UploadProvider;
  canvasSize: { height: number; width: number };
  selectedLibrary: string | undefined;
  filename: string;
  isPremium: boolean;
}

export const addLibraryImageToCanvas = (
  imageId: string,
  imageType: string,
  sourceType: VSLibrarySourceType,
  scribeId: number,
  canvasSize: { height: number; width: number },
  selectedLibrary: string | undefined,
  filename: string,
  provider: UploadProvider,
  isPremium = false
): AddLibraryImageToCanvasAction => ({
  type: ADD_LIBRARY_IMAGE_TO_CANVAS,
  imageId,
  imageType,
  sourceType,
  scribeId,
  provider,
  canvasSize,
  selectedLibrary,
  filename,
  isPremium
});

export interface DeleteUserImageAction {
  type: typeof DELETE_USER_IMAGE_REQUESTED;
  imageId: number;
  targetLibrary: VSCAssetImageSourceName;
}

export const deleteUserImage = (imageId: number, targetLibrary: VSCAssetImageSourceName): DeleteUserImageAction => ({
  type: DELETE_USER_IMAGE_REQUESTED,
  imageId,
  targetLibrary
});

interface DeleteUserImageFailure {
  type: typeof DELETE_USER_IMAGE_FAILURE;
  error: Error;
}

export const deleteUserImageFailure = (error: Error): DeleteUserImageFailure => ({
  type: DELETE_USER_IMAGE_FAILURE,
  error
});

export interface GetImageLibrariesAction {
  type: typeof GET_IMAGE_LIBRARIES;
}
export const getImageLibraries = () => ({
  type: GET_IMAGE_LIBRARIES
});

export interface GetImageLibrariesSuccessAction {
  type: typeof GET_IMAGE_LIBRARIES_SUCCESS;
  libraries: Array<ImageCategory>;
}

export const getImageLibrariesSuccess = (libraries: Array<ImageCategory>): GetImageLibrariesSuccessAction => ({
  type: GET_IMAGE_LIBRARIES_SUCCESS,
  libraries
});

export interface GetImageLibrariesFailureAction {
  type: typeof GET_IMAGE_LIBRARIES_FAILURE;
  error: Error;
}

export const getImageLibrariesFailure = (error: Error): GetImageLibrariesFailureAction => ({
  type: GET_IMAGE_LIBRARIES_FAILURE,
  error
});

export interface DeleteUserImageSuccessAction {
  type: typeof DELETE_USER_IMAGE_SUCCESS;
  imageId: number;
  targetLibrary: VSCAssetImageSourceName;
}

export const deleteUserImageSuccess = (
  imageId: number,
  targetLibrary: VSCAssetImageSourceName
): DeleteUserImageSuccessAction => ({
  type: DELETE_USER_IMAGE_SUCCESS,
  imageId,
  targetLibrary
});

export interface UploadImageFilesAction {
  type: typeof UPLOAD_IMAGE_FILES;
  scribeId: number;
  files: FileList;
  uploadMethod: string;
}

type UploadImageFilesProperties = Omit<UploadImageFilesAction, 'type'>;

export const uploadImageFiles = ({
  scribeId,
  files,
  uploadMethod
}: UploadImageFilesProperties): UploadImageFilesAction => ({
  type: UPLOAD_IMAGE_FILES,
  scribeId,
  files,
  uploadMethod
});

interface UploadLibraryImageAssetAction {
  type: typeof UPLOAD_LIBRARY_IMAGE_ASSET;
  file: File;
  filename: string;
  scribeId: number;
  originalAssetId: number;
  source?: string;
  sourceIdentifier?: string;
  isPremium: boolean;
}

export const uploadLibraryImageAsset = (
  file: File,
  filename: string,
  scribeId: number,
  originalAssetId: number,
  source?: string,
  sourceIdentifier?: string,
  isPremium = false
): UploadLibraryImageAssetAction => ({
  type: UPLOAD_LIBRARY_IMAGE_ASSET,
  file,
  filename,
  scribeId,
  originalAssetId,
  source,
  sourceIdentifier,
  isPremium
});

interface UploadImageAssetOnLoadAction {
  type: typeof UPLOAD_IMAGE_ASSET_ON_LOAD;
  libraryElements: Array<ScribeImageElementModelProps>;
  scribeId: number;
}

export const uploadImageAssetOnLoad = (
  libraryElements: Array<ScribeImageElementModelProps>,
  scribeId: number
): UploadImageAssetOnLoadAction => ({
  type: UPLOAD_IMAGE_ASSET_ON_LOAD,
  libraryElements,
  scribeId
});

export interface UploadImageAssetSuccessAction {
  type: typeof UPLOAD_IMAGE_SUCCESS;
  scribeId: number;
  assetId?: string | number;
  originalAssetId?: string | number;
  source?: string;
}

export const uploadImageAssetSuccess = (
  scribeId: number,
  assetId?: string | number,
  originalAssetId?: string | number,
  source?: string
): UploadImageAssetSuccessAction => ({
  type: UPLOAD_IMAGE_SUCCESS,
  scribeId,
  assetId,
  originalAssetId,
  source
});

export const uploadImageAssetFailed = () => ({
  type: UPLOAD_IMAGE_FAILED
});

interface UploadUserImageAssetAction {
  type: typeof UPLOAD_USER_IMAGE_ASSET;
  file: File;
  filename: string;
  scribeId: number;
  originalAssetId: number;
}

export const uploadUserImageAsset = (
  file: File,
  filename: string,
  scribeId: number,
  originalAssetId: number
): UploadUserImageAssetAction => ({
  type: UPLOAD_USER_IMAGE_ASSET,
  file,
  filename,
  scribeId,
  originalAssetId
});

export interface FullyVectorizeImageAndReplaceElementAction {
  type: typeof FULLY_VECTORIZE_IMAGE_AND_REPLACE_ELEMENT;
  elementId: string;
  scribeId: number;
}
export const fullyVectorizeImageAndReplaceElement = (
  elementId: string,
  scribeId: number
): FullyVectorizeImageAndReplaceElementAction => ({
  type: FULLY_VECTORIZE_IMAGE_AND_REPLACE_ELEMENT,
  elementId,
  scribeId
});

export interface PrependUserImagesListAction {
  type: typeof PREPEND_USER_IMAGES_LIST;
  images: Array<VSCAssetImageData>;
  targetLibrary: VSCAssetImageSourceName;
}

export const prependUserImagesList = (
  images: Array<VSCAssetImageData>,
  targetLibrary: VSCAssetImageSourceName
): PrependUserImagesListAction => ({
  type: PREPEND_USER_IMAGES_LIST,
  images,
  targetLibrary
});

export interface StartReplaceImageAction {
  type: typeof START_REPLACE_IMAGE;
  elementId: string;
}

export const startReplaceImage = (elementId: string): StartReplaceImageAction => ({
  type: START_REPLACE_IMAGE,
  elementId
});

export interface ClearReplaceImageAction {
  type: typeof CLEAR_REPLACE_IMAGE;
  focusActiveElement?: boolean;
}

export const clearReplaceImage = ({
  focusActiveElement
}: {
  focusActiveElement?: boolean;
} = {}): ClearReplaceImageAction => ({
  type: CLEAR_REPLACE_IMAGE,
  focusActiveElement
});

export interface UploadImageToLibraryAction {
  type: typeof UPLOAD_IMAGE_TO_LIBRARY;
  files: FileList;
}

export const uploadImageToLibrary = (files: FileList): UploadImageToLibraryAction => ({
  type: UPLOAD_IMAGE_TO_LIBRARY,
  files
});

export interface RenameUserImageAction {
  type: typeof RENAME_USER_IMAGE;
  imageId: number;
  filename: string;
  targetLibrary: VSCAssetImageSourceName;
}

export const renameUserImage = (
  imageId: number,
  filename: string,
  targetLibrary: VSCAssetImageSourceName
): RenameUserImageAction => ({
  type: RENAME_USER_IMAGE,
  imageId,
  filename,
  targetLibrary
});

export interface RenameUserImageSuccessAction {
  type: typeof RENAME_USER_IMAGE_SUCCESS;
  imageId: number;
  filename: string;
  targetLibrary: VSCAssetImageSourceName;
}

export const renameUserImageSuccess = (
  imageId: number,
  filename: string,
  targetLibrary: VSCAssetImageSourceName
): RenameUserImageSuccessAction => ({
  type: RENAME_USER_IMAGE_SUCCESS,
  imageId,
  filename,
  targetLibrary
});

export const SORT_IMAGE_ASSETS = 'SORT_IMAGE_ASSETS';

export interface SortImageAssetsAction {
  type: typeof SORT_IMAGE_ASSETS;
  sortOrder: ImageAssetSortOrder;
}

export const sortImageAssets = (sortOrder: ImageAssetSortOrder): SortImageAssetsAction => ({
  type: SORT_IMAGE_ASSETS,
  sortOrder
});

export interface FetchImageLibraryPageAction {
  type: typeof FETCH_IMAGE_LIBRARY_PAGE;
  queryKey: ImageLibraryQueryKey;
  offset: number;
  limit: number;
}

export const fetchImageLibraryPage = (
  queryKey: ImageLibraryQueryKey,
  offset: number,
  limit = 30
): FetchImageLibraryPageAction => ({
  type: FETCH_IMAGE_LIBRARY_PAGE,
  queryKey,
  offset,
  limit
});

export interface FetchImageLibraryPageSuccessAction {
  type: typeof FETCH_IMAGE_LIBRARY_PAGE_SUCCESS;
  cacheKey: string;
  items: VSCAssetPageItem[];
  total: number;
  hasMore: boolean;
  expiresAt: number;
}

export const fetchImageLibraryPageSuccess = (
  cacheKey: string,
  items: VSCAssetPageItem[],
  total: number,
  hasMore: boolean,
  expiresAt: number
): FetchImageLibraryPageSuccessAction => ({
  type: FETCH_IMAGE_LIBRARY_PAGE_SUCCESS,
  cacheKey,
  items,
  total,
  hasMore,
  expiresAt
});

export interface FetchImageLibraryPageFailureAction {
  type: typeof FETCH_IMAGE_LIBRARY_PAGE_FAILURE;
  error: string;
}

export const fetchImageLibraryPageFailure = (error: string): FetchImageLibraryPageFailureAction => ({
  type: FETCH_IMAGE_LIBRARY_PAGE_FAILURE,
  error
});

export interface InvalidateImageLibraryCacheAction {
  type: typeof INVALIDATE_IMAGE_LIBRARY_CACHE;
  sources?: VSCAssetImageSourceName[];
}

export const invalidateImageLibraryCache = (
  sources?: VSCAssetImageSourceName[]
): InvalidateImageLibraryCacheAction => ({
  type: INVALIDATE_IMAGE_LIBRARY_CACHE,
  sources
});
